/**
 * Changing your own password: on its own page when an administrator set it
 * (or for a new account's first sign-in), and from your account at any time;
 * My account also holds two-step sign-in and email notifications (1.0.0-rc.4).
 */
import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2 } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import { useMe, useRightsSettings, type Me } from '../api/hooks';
import { can } from '../lib/permissions';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { queryKeys } from '../api/queryKeys';
import { Segmented } from './Segmented';
import { MfaSection } from './Mfa';
// 1.6.0 (T, pages/account/AssistantAccess.tsx): My account → AI assistant access.
import { AssistantAccessTab, useAssistantTabVisible } from '../pages/account/AssistantAccess';

function PasswordForm({ me, forced, onDone, onCancel }: { me: Me; forced: boolean; onDone: () => void; onCancel?: () => void }) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = again.length > 0 && again !== next;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== again) return setError('The new passwords are not the same');
    setBusy(true);
    setError(null);
    try {
      await api('/api/v1/me/password', { method: 'POST', json: { currentPassword: current, newPassword: next } });
      await qc.invalidateQueries({ queryKey: queryKeys.me() });
      toast('Password changed');
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" style={{ gap: 12 }} onSubmit={submit} data-testid="change-password">
      {forced && <p className="muted" style={{ margin: 0 }}>An administrator set your password. Choose your own to continue, {me.user.displayName}.</p>}
      {error && <div className="banner error" role="alert">{error}</div>}
      <input type="email" autoComplete="username" value={me.user.email} readOnly hidden />
      <div className="field"><label htmlFor="pw-current">{forced ? 'Password you were given' : 'Current password'}</label>
        <input id="pw-current" className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus /></div>
      <div className="field"><label htmlFor="pw-new">New password</label>
        <input id="pw-new" className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} />
        <span className="help">At least 10 characters. A few words together are easy to remember and hard to guess.</span></div>
      <div className="field"><label htmlFor="pw-again">New password again</label>
        <input id="pw-again" className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
        {mismatch && <span className="help" style={{ color: 'var(--danger)' }}>Not the same as above</span>}</div>
      <p className="faint" style={{ margin: 0, fontSize: 12 }}>You stay signed in here; other devices are signed out.</p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>}
        <button className="btn primary" type="submit" disabled={busy || mismatch || next.length < 10} data-testid="change-password-save">
          {busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />} Change password
        </button>
      </div>
    </form>
  );
}

/** Full page shown instead of PRISM until a password set by an administrator is changed. */
export function ForcedPasswordChange({ me }: { me: Me }) {
  const qc = useQueryClient();
  async function signOut() {
    await api('/api/v1/auth/logout', { method: 'POST', json: {} }).catch(() => undefined);
    qc.clear();
    window.location.assign('/login');
  }
  return (
    <div className="login">
      <div className="login-art" aria-hidden>
        <div className="brand"><img src="/prism.svg" width={30} height={30} alt="" /> PRISM</div>
        <div className="promise">Find any moment. <span>Make it a story.</span> Deliver it anywhere.</div>
        <div className="strip">{Array.from({ length: 5 }, (_, i) => <div key={i} />)}</div>
      </div>
      <div className="login-form-wrap">
        <div className="login-form">
          <h1 style={{ margin: 0 }}>Choose a new password</h1>
          <PasswordForm me={me} forced onDone={() => undefined} />
          <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

/** Email notifications (1.0.0-rc.4): what PRISM mails you about, when email is set up. */
function EmailPreferences() {
  const qc = useQueryClient();
  // 1.8.0: canRights and preferences.rights (the rights digest, on by default) when the server sends them.
  const q = useQuery({ queryKey: ['me-email-prefs'], queryFn: () => api<{ configured: boolean; email: string; canAlerts: boolean; canIntake?: boolean; canRights?: boolean; preferences: Record<'approvals' | 'tasks' | 'shares' | 'alerts' | 'intake', boolean> & { rights?: boolean } }>('/api/v1/me/email-preferences') });
  // The rights topic: for people with Manage rights, while rights management and its daily email are both on.
  const rightsSettings = useRightsSettings().data;
  const rightsTopic = can(useMe().data, 'rights.manage') && Boolean(rightsSettings?.enabled) && rightsSettings?.digestEnabled !== false;
  if (!q.data) return <div className="skeleton" style={{ height: 80 }} />;
  const d = { ...q.data, preferences: { ...q.data.preferences, rights: q.data.preferences.rights ?? true } };
  const set = async (key: string, value: boolean) => {
    try {
      await api('/api/v1/me/email-preferences', { method: 'PUT', json: { [key]: value } });
      await qc.invalidateQueries({ queryKey: ['me-email-prefs'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const rows: Array<[keyof typeof d.preferences, string, string]> = [
    ['approvals', 'Approvals', 'Someone asks you to approve something, or decides on what you asked'],
    ['tasks', 'Tasks', 'A task is assigned to you or to a group you are in'],
    ['shares', 'Shares', 'Someone shares a collection with you'],
    ...(d.canAlerts ? [['alerts', 'System status alerts', 'New alerts in System status (for administrators), at most once a day each'] as [keyof typeof d.preferences, string, string]] : []),
    ...(d.canIntake ? [['intake', 'Intake', 'A media portal upload is waiting for you, a supplier replies, and a daily digest of overdue deliveries'] as [keyof typeof d.preferences, string, string]] : []),
    ...(rightsTopic && d.canRights !== false ? [['rights', 'Rights', 'A daily email of rights ending soon, rights that ended, and finished assets a source turned red'] as [keyof typeof d.preferences, string, string]] : []),

  ];
  return (
    <div className="form" style={{ gap: 10 }} data-testid="email-preferences">
      {!d.configured && <div className="banner info"><div className="banner-body"><span>Email is not set up for this installation, so PRISM sends none; you see everything in the app. These choices apply once an administrator sets it up.</span></div></div>}
      <p className="muted" style={{ margin: 0 }}>Mail goes to <b>{d.email}</b>.</p>
      {rows.map(([key, label, help]) => (
        <label className="toggle-row" key={key}><input type="checkbox" checked={d.preferences[key]} onChange={(e) => void set(key, e.target.checked)} data-testid={`email-pref-${key}`} />
          <span><b>{label}</b><br /><span className="faint" style={{ fontSize: 12 }}>{help}</span></span></label>
      ))}
    </div>
  );
}

interface SsoLinks {
  canLink: boolean;
  providers: Array<{ id: string; name: string; linked: boolean; linkedEmail: string | null; linkedAt: string | null; linkUrl: string | null }>;
}

/** Why Link single sign-on did not work, from the #sso-link-error=… the API sends the browser back with. */
const SSO_LINK_ERRORS: Record<string, string> = {
  link_email_mismatch: 'Your organisation’s sign-in service signed in a different email address than this PRISM account’s, so nothing was linked.',
  link_taken: 'That sign-in is already linked to another PRISM account.',
  link_needs_session: 'Stay signed in to PRISM in this browser while you link single sign-on, then try again.',
  state_expired: 'Linking took too long or was already used. Start again.',
  wrong_browser: 'Linking was started in another browser or window. Start again here.',
  email_unverified: 'Your organisation’s sign-in service has not verified your email address.',
  no_email: 'Your organisation’s sign-in service did not tell PRISM your email address.',
  provider_unreachable: 'Your organisation’s sign-in service could not be reached. Try again in a moment.',
  provider_error: 'Your organisation’s sign-in service refused the sign-in.',
  token_invalid: 'The answer from your organisation’s sign-in service could not be verified, so nothing was linked.',
  unknown_provider: 'That sign-in option is not available.',
};

const linkHash = () => /(?:^#|&)(sso-linked|sso-link-error)=([^&]*)/.exec(window.location.hash);

/** Whether the address says Link single sign-on just came back (My account opens to show how it went). */
export const ssoLinkReturned = () => Boolean(linkHash());

/** The outcome of Link single sign-on in the address, read once and taken out of it. */
function takeSsoLinkResult(): { ok: boolean; text: string } | null {
  const m = linkHash();
  if (!m) return null;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  const value = decodeURIComponent(m[2]!);
  return m[1] === 'sso-linked'
    ? { ok: true, text: `Single sign-on with ${value} is linked: you can now sign in with it.` }
    : { ok: false, text: SSO_LINK_ERRORS[value] ?? 'Single sign-on could not be linked.' };
}

/**
 * Single sign-on on My account (1.0.0-rc.14): an account with a password or administrator rights is never linked to an
 * identity provider by email alone; its owner links it here, signed in, with a fresh sign-in at the provider.
 */
function SsoLinkSection({ result }: { result: { ok: boolean; text: string } | null }) {
  const q = useQuery({ queryKey: ['me-sso-links'], queryFn: () => api<SsoLinks>('/api/v1/me/sso-links') });
  const providers = q.data?.providers ?? [];
  if (!result && !providers.length) return null;
  return (
    <div data-testid="sso-links">
      <p className="section-label" style={{ marginTop: 12 }}>Single sign-on</p>
      {result && <div className={`banner ${result.ok ? 'info' : 'warning'}`} role="status" data-testid="sso-link-result"><div className="banner-body"><span>{result.text}</span></div></div>}
      {providers.map((p) => (
        <div key={p.id} className="toggle-row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <span><b>{p.name}</b><br /><span className="faint" style={{ fontSize: 12 }}>
            {p.linked ? `Linked${p.linkedEmail ? ` as ${p.linkedEmail}` : ''}${p.linkedAt ? ` since ${new Date(p.linkedAt).toLocaleDateString()}` : ''}` : 'Not linked: sign in there once to link it to this account'}
          </span></span>
          {!p.linked && p.linkUrl && (
            <button type="button" className="btn sm" data-testid={`sso-link-${p.id}`} onClick={() => { window.location.href = p.linkUrl!; }}>
              <KeyRound size={14} /> Link single sign-on
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** My account: who you are signed in as, your password, two-step sign-in and email (1.0.0-rc.4). */
export function AccountDialog({ me, onClose }: { me: Me; onClose: () => void }) {
  const [tab, setTab] = useState<'account' | 'mfa' | 'email' | 'assistants'>('account');
  const [linkResult] = useState(takeSsoLinkResult);
  const sso = me.session?.authMethod === 'sso';
  // 1.6.0: shown when the workspace allows AI assistant access (T).
  const assistants = useAssistantTabVisible(me);
  return (
    <Modal title="My account" onClose={onClose} wide>
      <Segmented role="tablist" buttonRole="tab" buttonType="button" value={tab} onChange={setTab} style={{ alignSelf: 'flex-start' }}
        options={[{ value: 'account', label: 'Account', testId: 'account-tab-account' }, { value: 'mfa', label: 'Two-step sign-in', testId: 'account-tab-mfa' }, { value: 'email', label: 'Email notifications', testId: 'account-tab-email' },
          ...(assistants ? [{ value: 'assistants' as const, label: 'AI assistant access', testId: 'account-tab-assistants' }] : [])]} />
      {tab === 'account' && (
        <>
          <dl className="kv" style={{ marginTop: 0 }}>
            <dt>Name</dt><dd>{me.user.displayName}</dd>
            <dt>Email</dt><dd>{me.user.email}</dd>
            <dt>Role</dt><dd>{me.roleLabel} · {me.workspace.name}</dd>
            {sso && <><dt>Signed in</dt><dd>Through your organisation’s single sign-on</dd></>}
          </dl>
          {me.hasPassword === false ? (
            <p className="muted">Your account has no PRISM password: you sign in through your organisation’s identity provider.</p>
          ) : (
            <>
              <p className="section-label" style={{ marginTop: 12 }}>Change password</p>
              <PasswordForm me={me} forced={false} onDone={onClose} onCancel={onClose} />
            </>
          )}
          <SsoLinkSection result={linkResult} />
        </>
      )}
      {tab === 'mfa' && <MfaSection />}
      {tab === 'email' && <EmailPreferences />}
      {tab === 'assistants' && assistants && <AssistantAccessTab me={me} />}
    </Modal>
  );
}
